import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Stack, TextField, TextFieldProps, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ListingEditContext } from '../../../context/Listing/ListingEditFlow';
import { getListingInfoFromStatus, getListingInfoFromType, GETTING_AROUND_MAX_LENGTH, isDescriptionValid, isListingTypeValid, isShortDescriptionValid, LONG_DESC_MAX_LENGTH, NEIGHBORHOOD_DESCRIPTION_MAX_LENGTH, SHORT_DESC_MAX_LENGTH, TITLE_MAX_LENGTH, useGettingAroundDescriptionEffect, useGettingAroundDescriptionOnBlurCallback, useHandleGettingAroundDescriptionOnChangeCallback, useHandleLongDescriptionOnBlurCallback, useHandleLongDescriptionOnChangeCallback, useHandleNeighborhoodDescriptionOnChangeCallback, useHandleShortDescriptionOnBlurCallback, useHandleShortDescriptionOnChangeCallback, useHandleTitleOnBlurCallback, useHandleTitleOnChangeCallback, useListingTypeEffect, useListingTypeOnChangeCallback, useNeighborhoodDescriptionEffect, useNeighborhoodDescriptionOnBlurCallback, useTitleEffect } from '../../../utilities/listings';
import { Info } from '@mui/icons-material';
import { ListingTree, ListingType, TListingStatus } from '../../../types/db';
import { ListingTypeCard } from '../../../components/listing/ListingTypeCard';
import { useDropzone } from 'react-dropzone';
import { ListingImageThumbnail } from '../../../components/listing/ListingImageThumbnail';
import { ListingEditSectionView } from './ListingEditSectionView';
import { ListingImageThumbnailEdit } from '../../../components/listing/ListingImageThumbnail/ListingImageThumbnail';
import { useListingEditPhotos } from '../../../hooks/listing/useListingEditPhotos';
import { removeKeysFromObject } from '../../../types/utils/objectMutation';
import { ROUTE_PATH } from '../../../utilities/navigation';
import { useNavigate } from 'react-router';

/** The details of the listing management/edit */
export const ListingDetailsPage = () => {
    return <Container />
}

const Container = () => {
    const { t } = useTranslation();
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { isLoading } = listingEditContext;
    if (isLoading) {
        return <CircularProgress />
    }

    return (
        <Stack direction={'column'} spacing={2}>
            <ListingEditSectionView title={t('listing.edit.basicsTitle')} level={'L1'}>
                <ListingTitleContainer />
                <ListingDescriptionsContainer />
                <ListingStatusContainer />
            </ListingEditSectionView >
            <Divider />
            <ListingPhotosContainer />
            <Divider />
            <ListingEditSectionView title={t('listing.edit.locationTitle')} level={'L1'}>
                <ListingLocationAddressContainer />
                <ListingLocationNeighborhoodContainer />
                <ListingLocationGettingAroundContainer />
            </ListingEditSectionView >
            <Divider />
            <ListingEditSectionView title={t('listing.edit.propertyTitle')} level={'L1'}>
                <ListingPropertyTypeContainer />
                <ListingPropertyRoomsContainer />
            </ListingEditSectionView >
        </Stack>
    )
}

const ListingTitleContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing, setActivePageInfo, handleSave, handleCancel, completed } = listingEditContext;
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);

    const [titleError, setTitleError] = useState<string>();

    const titleProps: TextFieldProps = {
        value: listing?.title || '',
        helperText: titleError || `${listing?.title?.length || 0}/${TITLE_MAX_LENGTH}`,
        error: !!titleError,
    };

    useTitleEffect(listing?.title, setActivePageInfo, editing);
    const handleTitleOnBlur = useHandleTitleOnBlurCallback(t, setTitleError);
    const handleTitleOnChange = useHandleTitleOnChangeCallback(setActivePageInfo, setTitleError);

    return <ListingEditSectionView title={t('listing.edit.titleTitle')}
        isEditMode={editing}
        onEditClick={() => setEditing(true)}
        onCancelClick={() => { handleCancel("title"); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {editing ?
            <TextField
                label={t('listing.creation.titleInputLabel')}
                placeholder={t('listing.creation.titleInputPlaceholder')}
                onBlur={handleTitleOnBlur}
                onChange={handleTitleOnChange}
                autoComplete={'off'}
                {...titleProps}
            />
            :
            <Typography>{titleProps.value}</Typography>
        }
    </ListingEditSectionView>
}

const ListingDescriptionsContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing, handleSave, handleCancel, setActivePageInfo, completed } = listingEditContext;

    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);

    const [descError, setDescError] = useState<string>();
    const [desc2Error, setDesc2Error] = useState<string>();

    const descProps: TextFieldProps = {
        value: listing?.short_description || '',
        helperText: descError || `${listing?.short_description?.length || 0}/${SHORT_DESC_MAX_LENGTH}`,
        error: !!descError,
    }
    const desc2Props: TextFieldProps = {
        value: listing?.description || '',
        helperText: desc2Error || `${listing?.description?.length || 0}/${LONG_DESC_MAX_LENGTH}`,
        error: !!desc2Error,
    };

    useEffect(() => {
        if (!editing) return;//if not editing, we don't check
        if (isShortDescriptionValid(listing?.short_description) && isDescriptionValid(listing?.description)) {
            setActivePageInfo({}, true)
        } else {
            setActivePageInfo({}, false)
        }
    }, [listing?.short_description, listing?.description, setActivePageInfo, editing]);

    const handleDescOnBlur = useHandleShortDescriptionOnBlurCallback(t, setDescError);
    const handleDescOnChange = useHandleShortDescriptionOnChangeCallback(setActivePageInfo, setDescError);

    const handleDesc2OnBlur = useHandleLongDescriptionOnBlurCallback(t, setDesc2Error);
    const handleDesc2OnChange = useHandleLongDescriptionOnChangeCallback(setActivePageInfo, setDesc2Error);

    return <ListingEditSectionView title={t('listing.edit.descriptionTitle')}
        isEditMode={editing}
        onEditClick={() => setEditing(true)}
        onCancelClick={() => { handleCancel(); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {editing ?
            <>
                <TextField
                    label={t('listing.creation.shortDescriptionInputLabel')}
                    placeholder={t('listing.creation.shortDescriptionInputPlaceholder')}
                    multiline={true}
                    onBlur={handleDescOnBlur}
                    onChange={handleDescOnChange}
                    {...descProps}
                />
                <TextField
                    label={t('listing.creation.descriptionLabel')}
                    placeholder={t('listing.creation.descriptionPlaceholder')}
                    multiline={true}
                    onBlur={handleDesc2OnBlur}
                    onChange={handleDesc2OnChange}
                    {...desc2Props}
                />
            </>
            :
            <>
                <Typography>{listing?.short_description || ''}</Typography>
                <Typography sx={{ wordBreak: 'break-word' }}>{listing?.description || ''}</Typography>
            </>
        }
    </ListingEditSectionView>
}

const ListingStatusContainer = () => {
    const navigate = useNavigate();
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing, handleSave, handleCancel, setActivePageInfo, completed } = listingEditContext;
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [deleteListingDialog, setDeleteListingDialog] = useState({ open: false, checked: false });

    const statusInfo = getListingInfoFromStatus(listing?.status || 'in-review', t);

    const renderRadioElement = (s: TListingStatus, disabled?: boolean) => {
        const info = getListingInfoFromStatus(s, t);
        return <FormControlLabel value={s}
            control={<Radio />}
            disabled={disabled}
            label={
                <Stack direction={'column'}>
                    <Stack direction={'row'} spacing={1}>
                        {info.icon}
                        <Typography>{info.title}</Typography>
                    </Stack>
                    <Typography variant='caption'>{info.description}</Typography>
                </Stack>
            }
            sx={{ alignItems: 'start' }} />
    }

    return <>
        <ListingEditSectionView title={t('listing.edit.statusTitle')}
            isEditMode={editing}
            onEditClick={() => setEditing(true)}
            onCancelClick={() => { handleCancel("status"); setEditing(false) }}
            onSaveClick={() => {
                if (listing?.status === 'de-activated') {
                    setDeleteListingDialog({ open: true, checked: false });
                    return;
                }
                if (!completed) return;
                handleSave().then(() => setEditing(false))
            }}
            isSaveDisabled={!completed && listing.status !== 'de-activated'}>
            {editing ?
                <FormControl>
                    {
                        (listing?.status === 'in-review' || listing?.status === 'pending') &&
                        <Alert icon={<Info fontSize="inherit" />} severity="warning" sx={{ m: 1 }}>
                            {t('listing.status.alertActionDisabled')}
                        </Alert>
                    }
                    <RadioGroup
                        value={listing?.status}
                        onChange={(_, value) => setActivePageInfo({ status: value as TListingStatus })}
                        sx={{ gap: 2 }}
                    >
                        {renderRadioElement('published', listing?.status === 'in-review' || listing?.status === 'pending' || listing?.status === 'de-activated')}
                        {listing?.status === 'in-review' && renderRadioElement('in-review', true)}
                        {listing?.status === 'pending' && renderRadioElement('pending', true)}
                        {/* {renderRadioElement('snoozed', status === 'in-review' || status === 'pending'||status==='de-activated')} */}
                        {renderRadioElement('un-listed', listing?.status === 'in-review' || listing?.status === 'pending' || listing?.status === 'de-activated')}
                        {renderRadioElement('de-activated')}
                    </RadioGroup>
                </FormControl>
                :
                <Stack direction={'row'} spacing={1}>
                    {statusInfo.icon}
                    <Typography>{`${statusInfo.title} - ${statusInfo.description}`}</Typography>
                </Stack>
            }
        </ListingEditSectionView>
        <Dialog open={deleteListingDialog.open}
            onClose={() => setDeleteListingDialog({ open: false, checked: false })}>
            <DialogTitle>
                {t('listing.status.warningDeletionTitle')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('listing.status.warningDeletionContent')}
                </DialogContentText>
                <FormControlLabel control={<Checkbox />}
                    checked={deleteListingDialog.checked}
                    onChange={(_, checked) => setDeleteListingDialog({ open: deleteListingDialog.open, checked })}
                    label={t('listing.status.warningDeletionFooter')} />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteListingDialog({ open: false, checked: false })} autoFocus>{t('cancel')}</Button>
                <Button disabled={!deleteListingDialog.checked} onClick={() => { handleSave().then(() => setDeleteListingDialog({ open: false, checked: false })).then(() => { navigate(ROUTE_PATH.listingsManagementBuild()) }) }} >{t('agree')}</Button>
            </DialogActions>
        </Dialog>
    </>
}

const ListingPhotosContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }

    const { listing, setActivePageInfo, handleCancel, handleSave, completed } = listingEditContext;
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);

    const {
        photos,
        deletePhoto,
        changePhotoDescription,
        onDropPhotos,
        orderPhotoDown,
        orderPhotoUp,
        orderPhotoToTop } = useListingEditPhotos(listing.id, listing.photos_ordered);

    useEffect(() => {
        if (!editing) return;//if not editing, we don't check
        if (photos.every(file => file.percent === 100 && !!file.path && !!file.id)) {
            setActivePageInfo({
                photos_ordered: photos.map(file => ({ ...removeKeysFromObject(file, ['percent', 'preview', 'error']) }))
            }, true);
        } else {
            setActivePageInfo({}, false);
        }
    }, [editing, photos, setActivePageInfo]);

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject } = useDropzone({
            noDrag: false,
            accept: {
                'image/*': ['.jpeg', '.png']
            },
            onDrop: onDropPhotos,
        });

    const style = useMemo(() => ({
        ...{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            padding: '20px',
            borderWidth: 2,
            borderRadius: 2,
            borderColor: '#eeeeee',
            borderStyle: 'dashed',
            backgroundColor: '#fafafa',
            color: '#bdbdbd',
            outline: 'none',
            transition: 'border .24s ease-in-out'
        },
        ...(isFocused ? { borderColor: '#2196f3' } : {}),
        ...(isDragAccept ? { borderColor: '#00e676' } : {}),
        ...(isDragReject ? { borderColor: '#ff1744' } : {})
    }), [
        isFocused,
        isDragAccept,
        isDragReject
    ]);

    return (
        <ListingEditSectionView title={t('listing.edit.photosTitle')} level={'L1'}
            isEditMode={editing}
            onEditClick={() => setEditing(true)}
            onCancelClick={() => { handleCancel(); setEditing(false) }}
            onSaveClick={() => {
                if (!completed) return;
                handleSave().then(() => setEditing(false))
            }}
            isSaveDisabled={!completed}>
            {
                editing ?
                    <section>
                        <div {...getRootProps({ style })}>
                            <input {...getInputProps()} />
                            <p>Drag 'n' drop some files here, or click to select files</p>
                        </div>
                        {/* Note: find a better way to fit height properly than rowSpacing */}
                        <Stack gap={2} my={2} justifyContent={'center'} alignItems={'stretch'}>
                            {
                                photos.map((file, index) =>
                                    <ListingImageThumbnailEdit
                                        key={`${file.name}-${file.id}`}
                                        name={file.name}
                                        description={file.description}
                                        url={file.preview}
                                        percent={file.percent}
                                        error={file.error}
                                        onDeleteClick={file.percent === 100 ? () => deletePhoto(file) : undefined}
                                        onOrderUpClick={index === 0 ? undefined : () => orderPhotoUp(file)}
                                        onOrderDownClick={index === photos.length - 1 ? undefined : () => orderPhotoDown(file)}
                                        onOrderTopClick={index === 0 ? undefined : () => orderPhotoToTop(file)}
                                        onDescriptionValueChanged={(newDescription) => changePhotoDescription(file, newDescription)}
                                    />
                                )
                            }
                        </Stack>
                    </section>
                    :
                    <Grid container spacing={2} rowSpacing={4} my={2} justifyContent={'center'} alignItems={'stretch'}>
                        {
                            photos.map((file) =>
                                <Grid item
                                    key={`${file.name}-${file.id}`}
                                    xs={12} sm={6} md={3}>
                                    <ListingImageThumbnail
                                        name={file.name}
                                        description={file.description}
                                        url={file.preview}
                                    />
                                </Grid>
                            )
                        }
                    </Grid>
            }
        </ListingEditSectionView>
    )

}

const ListingLocationAddressContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing } = listingEditContext;
    const { t } = useTranslation();

    //TODO address view component
    return <ListingEditSectionView title={t('listing.edit.addressTitle')}>
        <Stack direction={'column'}>
            <Typography>{listing?.location?.address?.line1}</Typography>
            <Typography>{listing?.location?.address?.line2}</Typography>
            <Typography>{listing?.location?.address?.zip}</Typography>
            <Typography>{listing?.location?.address?.city}</Typography>
            <Typography>{listing?.location?.address?.country_name}</Typography>
        </Stack>
    </ListingEditSectionView>
}
const ListingLocationNeighborhoodContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing, handleSave, handleCancel, setActivePageInfo, completed } = listingEditContext;
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);

    const [textError, setTextError] = useState<string>();

    const textProps: TextFieldProps = {
        value: listing?.location?.neighborhood || '',
        helperText: textError || `${listing?.location?.neighborhood?.length || 0}/${NEIGHBORHOOD_DESCRIPTION_MAX_LENGTH}`,
        error: !!textError,
    };

    useNeighborhoodDescriptionEffect(listing?.location?.neighborhood || '', setActivePageInfo, editing)
    const handleTextOnBlur = useNeighborhoodDescriptionOnBlurCallback(t, setTextError);
    const handleTextOnChange = useHandleNeighborhoodDescriptionOnChangeCallback(setActivePageInfo, setTextError);

    return <ListingEditSectionView title={t('listing.edit.neighborhoodTitle')}
        isEditMode={editing}
        onEditClick={() => setEditing(true)}
        onCancelClick={() => { handleCancel(); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {
            editing ?
                <TextField
                    label={t('listing.edit.neighborhoodInputLabel')}
                    placeholder={t('listing.edit.neighborhoodInputPlaceholder')}
                    onBlur={handleTextOnBlur}
                    onChange={handleTextOnChange}
                    multiline={true}
                    {...textProps}
                />
                :
                <Typography>{listing?.location?.neighborhood || ''}</Typography>
        }
    </ListingEditSectionView>
}

const ListingLocationGettingAroundContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing, handleSave, handleCancel, setActivePageInfo, completed } = listingEditContext;
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);

    const [textError, setTextError] = useState<string>();

    const textProps: TextFieldProps = {
        value: listing?.location?.getting_around || '',
        helperText: textError || `${listing?.location?.getting_around?.length || 0}/${GETTING_AROUND_MAX_LENGTH}`,
        error: !!textError,
    };

    useGettingAroundDescriptionEffect(listing?.location?.getting_around || '', setActivePageInfo, editing)
    const handleTextOnBlur = useGettingAroundDescriptionOnBlurCallback(t, setTextError);
    const handleTextOnChange = useHandleGettingAroundDescriptionOnChangeCallback(setActivePageInfo, setTextError);

    return <ListingEditSectionView title={t('listing.edit.gettingAroundTitle')}
        isEditMode={editing}
        onEditClick={() => setEditing(true)}
        onCancelClick={() => { handleCancel(); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {
            editing ?
                <TextField
                    label={t('listing.edit.gettingAroundInputLabel')}
                    placeholder={t('listing.edit.gettingAroundInputPlaceholder')}
                    onBlur={handleTextOnBlur}
                    onChange={handleTextOnChange}
                    multiline={true}
                    {...textProps}
                />
                :
                <Typography>{listing?.location?.getting_around || ''}</Typography>
        }
    </ListingEditSectionView>
}

const ListingPropertyTypeContainer = () => {
    const listingEditContext = useContext(ListingEditContext);
    if (!listingEditContext) {
        throw new Error('Need ListingEditContext');
    }
    const { listing, handleSave, handleCancel, setActivePageInfo, completed } = listingEditContext;
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);

    const typeInfo = getListingInfoFromType(listing?.type || 'multi-mix', t);
    const parentType: ListingType | undefined = useMemo(() =>
        listing?.breadcrumbs?.length ? (listing?.breadcrumbs[listing.breadcrumbs.length - 1]?.type || 'multi-mix') : undefined,
        [listing?.breadcrumbs]);
    const childrenTypes: ListingType[] | undefined = useMemo(() =>
        !!listing?.sub_listings?.length ?
            listing?.sub_listings.map(child => child?.type).filter(t => !!t) as ListingType[]
            : undefined,
        [listing?.sub_listings]);

    useListingTypeEffect(parentType, listing?.type, childrenTypes, setActivePageInfo, editing);

    const handleTypeClick = useListingTypeOnChangeCallback(setActivePageInfo);

    //the option available to potentially change to
    const options: ListingType[] = (!parentType ?
        //if no parent, a parent itself
        ListingTree["multi-mix"]
            .filter(type => type.includes("multi-"))
        //there is a parent, take potential sub-listing for this parent
        : ListingTree[parentType])
        //filter the options compatible with children
        .filter(t => isListingTypeValid(parentType, t, childrenTypes));
    //only have the availability to edit if there is more than one option to change to
    const hasOptionToChange = options.length > 1;

    return <ListingEditSectionView title={t('listing.edit.typeTitle')}
        isEditMode={editing}
        onEditClick={hasOptionToChange ? () => setEditing(true) : undefined}
        onCancelClick={() => { handleCancel("status"); setEditing(false) }}
        onSaveClick={() => {
            if (!completed) return;
            handleSave().then(() => setEditing(false))
        }}
        isSaveDisabled={!completed}>
        {
            editing ?
                <Grid container spacing={2} justifyContent={'center'}>
                    {
                        options
                            .map(itemType => <Grid key={itemType} item xs={12} sm={6} md={4}><ListingTypeCard key={itemType} type={itemType} selected={itemType === listing?.type} onClick={() => handleTypeClick(itemType)} /></Grid>)
                    }
                </Grid>
                :
                <Stack direction={'row'} alignItems={'start'} spacing={1}>
                    {typeInfo.icon}
                    <Stack direction={'column'}>
                        <Typography variant={'h5'} fontWeight={'bold'}>{typeInfo.title}</Typography>
                        <Typography>{typeInfo.description}</Typography>
                    </Stack>
                </Stack>
        }
    </ListingEditSectionView>
}
const ListingPropertyRoomsContainer = () => {
    //TODO
    // const { t } = useTranslation();
    // return <ListingEditSectionView title={t('listing.edit.spacesTitle')}>

    // </ListingEditSectionView>
    return null;
}
